class AuthorBooks extends HTMLElement {
  static get observedAttributes() {
    return ["data-author-handle", "data-base-collection-url"];
  }

  static CACHE_VERSION = 1;
  static CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

  #abortController;
  #requestToken = 0;

  connectedCallback() {
    this.#init();
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this.#init();
  }

  async #init() {
    const authorHandle = this.dataset.authorHandle;
    const authorId = this.dataset.authorId;
    const baseCollectionUrl = this.dataset.baseCollectionUrl;

    const listEl = this.querySelector('[ref="list"]');
    const loadingEl = this.querySelector('[ref="loading"]');
    const emptyEl = this.querySelector('[ref="empty"]');
    const viewAllAuthoredEl = this.querySelector('[ref="viewAllAuthored"]');
    const viewAllEditedEl = this.querySelector('[ref="viewAllEdited"]');

    if (!authorHandle || !baseCollectionUrl || !listEl) return;

    const cached = this.#readCache(authorHandle);
    if (cached) {
      listEl.innerHTML = cached.html;

      loadingEl?.setAttribute("hidden", "");

      const hasItems = cached.count > 0;
      if (!hasItems) emptyEl?.removeAttribute("hidden");
      else emptyEl?.setAttribute("hidden", "");

      if (viewAllAuthoredEl) {
        cached.authoredTotal > cached.count
          ? viewAllAuthoredEl.removeAttribute("hidden")
          : viewAllAuthoredEl.setAttribute("hidden", "");
      }
      if (viewAllEditedEl) {
        cached.editedTotal > cached.count
          ? viewAllEditedEl.removeAttribute("hidden")
          : viewAllEditedEl.setAttribute("hidden", "");
      }

      return;
    }

    this.#requestToken += 1;
    const token = this.#requestToken;

    if (this.#abortController) this.#abortController.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    loadingEl?.removeAttribute("hidden");
    emptyEl?.setAttribute("hidden", "");
    viewAllAuthoredEl?.setAttribute("hidden", "");
    viewAllEditedEl?.setAttribute("hidden", "");

    listEl.innerHTML = "";

    try {
      const initialLimit = Number(this.dataset.initialLimit || 12);

      // 1) Try Shopify collection filtering first (fast path)
      const fastPath = await this.#tryCollectionFiltering({
        baseCollectionUrl,
        authorHandle,
        authorId,
        initialLimit,
        signal,
      });

      // If the filter-based response appears trustworthy and complete (single page), we can finish here.
      // Otherwise, treat fast path as a seed for perceived performance and reconcile with scan mode.
      if (fastPath.nodes.length > 0) {
        if (token !== this.#requestToken) return;
        for (const node of fastPath.nodes) listEl.appendChild(node);

        // Only hide loading if we are done.
        if (fastPath.trusted && fastPath.maxPages <= 1) {
          loadingEl?.setAttribute("hidden", "");

          if (viewAllAuthoredEl && fastPath.authoredTotal > 0)
            viewAllAuthoredEl.removeAttribute("hidden");
          if (viewAllEditedEl && fastPath.editedTotal > 0)
            viewAllEditedEl.removeAttribute("hidden");

          this.#writeCache(authorHandle, {
            html: listEl.innerHTML,
            count: fastPath.nodes.length,
            authoredTotal: fastPath.authoredTotal,
            editedTotal: fastPath.editedTotal,
          });

          return;
        }
      }

      // 2) Fallback: scan paginated /collections/all and filter client-side using data-authors/data-editors.
      const scanned = await this.#scanAllProducts({
        baseCollectionUrl,
        authorHandle,
        initialLimit,
        signal,
      });

      if (token !== this.#requestToken) return;
      // Replace any seeded results with the reconciled, deterministic results.
      listEl.innerHTML = "";
      for (const node of scanned.nodes) listEl.appendChild(node);
      loadingEl?.setAttribute("hidden", "");

      if (scanned.nodes.length === 0) {
        emptyEl?.removeAttribute("hidden");
        return;
      }

      this.#writeCache(authorHandle, {
        html: listEl.innerHTML,
        count: scanned.nodes.length,
        authoredTotal: 0,
        editedTotal: 0,
      });

      // We can’t reliably link to a filtered collection if the filter value isn’t matching,
      // so only show view-all buttons when the fast path provided totals.
    } catch (error) {
      if (error?.name === "AbortError") return;
      loadingEl?.setAttribute("hidden", "");
      emptyEl?.removeAttribute("hidden");
    }
  }

  #cacheKey(authorHandle) {
    return `author-books:v${AuthorBooks.CACHE_VERSION}:${authorHandle}`;
  }

  #readCache(authorHandle) {
    try {
      const raw = sessionStorage.getItem(this.#cacheKey(authorHandle));
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      if (parsed.v !== AuthorBooks.CACHE_VERSION) return null;
      if (!parsed.ts || Date.now() - parsed.ts > AuthorBooks.CACHE_TTL_MS)
        return null;
      if (typeof parsed.html !== "string") return null;

      return {
        html: parsed.html,
        count: Number(parsed.count || 0),
        authoredTotal: Number(parsed.authoredTotal || 0),
        editedTotal: Number(parsed.editedTotal || 0),
      };
    } catch {
      return null;
    }
  }

  #writeCache(authorHandle, { html, count, authoredTotal, editedTotal }) {
    try {
      sessionStorage.setItem(
        this.#cacheKey(authorHandle),
        JSON.stringify({
          v: AuthorBooks.CACHE_VERSION,
          ts: Date.now(),
          html,
          count,
          authoredTotal,
          editedTotal,
        }),
      );
    } catch {
      // Ignore storage errors (private mode/quota/etc.)
    }
  }

  #buildSectionUrl(baseUrl, params) {
    const url = new URL(baseUrl, window.location.origin);

    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value);
    }

    return url.toString();
  }

  async #fetchProducts(url, signal) {
    const res = await fetch(url, { credentials: "same-origin", signal });
    if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, "text/html");

    const root = doc.querySelector(".author-books-products");
    const total = Number(root?.getAttribute("data-total") || 0);
    const page = Number(root?.getAttribute("data-page") || 1);
    const pages = Number(root?.getAttribute("data-pages") || 1);

    const nodes = root
      ? Array.from(root.querySelectorAll(".author-books-products__item"))
      : [];

    const items = nodes
      .map((node, index) => {
        const handle = node.getAttribute("data-product-handle");
        if (!handle) return null;

        const authors = node.getAttribute("data-authors") || "";
        const editors = node.getAttribute("data-editors") || "";

        // Import the node into the current document before appending.
        const imported = document.importNode(node, true);

        const bookItem = imported.querySelector(".author-page__book-item");
        if (!bookItem) return null;

        return { handle, authors, editors, node: bookItem, page, index };
      })
      .filter(Boolean);

    return { total, page, pages, items };
  }

  async #tryCollectionFiltering({
    baseCollectionUrl,
    authorHandle,
    authorId,
    initialLimit,
    signal,
  }) {
    const attempts = [];

    if (authorHandle) {
      attempts.push({
        authoredUrl: this.#buildSectionUrl(baseCollectionUrl, {
          "filter.p.m.books.authors": authorHandle,
          section_id: "author-books-products",
        }),
        editedUrl: this.#buildSectionUrl(baseCollectionUrl, {
          "filter.p.m.books.editors": authorHandle,
          section_id: "author-books-products",
        }),
      });
    }

    if (authorId) {
      attempts.push({
        authoredUrl: this.#buildSectionUrl(baseCollectionUrl, {
          "filter.p.m.books.authors": authorId,
          section_id: "author-books-products",
        }),
        editedUrl: this.#buildSectionUrl(baseCollectionUrl, {
          "filter.p.m.books.editors": authorId,
          section_id: "author-books-products",
        }),
      });
    }

    for (const attempt of attempts) {
      const [authored, edited] = await Promise.all([
        this.#fetchProducts(attempt.authoredUrl, signal),
        this.#fetchProducts(attempt.editedUrl, signal),
      ]);

      const authoredMatched = authored.items.filter((item) =>
        this.#itemMatchesAuthor(item, authorHandle),
      );
      const editedMatched = edited.items.filter((item) =>
        this.#itemMatchesAuthor(item, authorHandle),
      );

      const authoredHadMismatches =
        authored.items.length !== authoredMatched.length;
      const editedHadMismatches = edited.items.length !== editedMatched.length;

      const trusted = !authoredHadMismatches && !editedHadMismatches;
      const authoredTotal = trusted ? authored.total : 0;
      const editedTotal = trusted ? edited.total : 0;
      const maxPages = Math.max(
        Number(authored.pages || 1),
        Number(edited.pages || 1),
      );

      const unique = new Map();
      for (const item of authoredMatched) unique.set(item.handle, item);
      for (const item of editedMatched) unique.set(item.handle, item);

      const sortedItems = Array.from(unique.values()).sort((a, b) => {
        if (a.page !== b.page) return a.page - b.page;
        return a.index - b.index;
      });

      const nodes = sortedItems.slice(0, initialLimit).map((i) => i.node);

      if (nodes.length > 0 || trusted) {
        return {
          nodes,
          trusted,
          maxPages,
          authoredTotal,
          editedTotal,
        };
      }
    }

    return {
      nodes: [],
      trusted: false,
      maxPages: 1,
      authoredTotal: 0,
      editedTotal: 0,
    };
  }

  async #scanAllProducts({
    baseCollectionUrl,
    authorHandle,
    initialLimit,
    signal,
  }) {
    const collected = [];
    const bestByHandle = new Map();

    // Safety cap: don’t scan endlessly.
    const maxPages = Number(this.dataset.maxPages || 25);

    const scanConcurrency = Number(this.dataset.scanConcurrency || 4);

    // Always fetch page 1 first so we can read how many pages exist.
    const firstUrl = this.#buildSectionUrl(baseCollectionUrl, {
      page: "1",
      section_id: "author-books-products",
    });

    const firstRes = await this.#fetchProducts(firstUrl, signal);
    const totalPages = Math.min(Number(firstRes.pages || 1), maxPages);

    // Gather all pages (bounded) so we can deterministically pick the first N in collection order.
    const pageResults = [firstRes];
    if (totalPages <= 1) {
      // continue
    } else {
      const pageNumbers = [];
      for (let p = 2; p <= totalPages; p += 1) pageNumbers.push(p);

      let nextIndex = 0;
      const worker = async () => {
        while (nextIndex < pageNumbers.length) {
          if (signal?.aborted) return;

          const p = pageNumbers[nextIndex];
          nextIndex += 1;

          const url = this.#buildSectionUrl(baseCollectionUrl, {
            page: String(p),
            section_id: "author-books-products",
          });

          const res = await this.#fetchProducts(url, signal);
          pageResults.push(res);
        }
      };

      const workers = [];
      for (let i = 0; i < scanConcurrency; i += 1) workers.push(worker());
      await Promise.all(workers);
    }

    for (const res of pageResults) {
      for (const item of res.items) {
        if (!this.#itemMatchesAuthor(item, authorHandle)) continue;

        const existing = bestByHandle.get(item.handle);
        if (!existing) {
          bestByHandle.set(item.handle, item);
          continue;
        }

        const aKey = existing.page * 1000 + existing.index;
        const bKey = item.page * 1000 + item.index;
        if (bKey < aKey) bestByHandle.set(item.handle, item);
      }
    }

    for (const item of bestByHandle.values()) collected.push(item);

    collected.sort((a, b) => {
      if (a.page !== b.page) return a.page - b.page;
      return a.index - b.index;
    });

    const nodes = collected.slice(0, initialLimit).map((i) => i.node);
    return { nodes };
  }

  #pipeListContains(pipeList, value) {
    if (!pipeList || !value) return false;
    const haystack = `|${pipeList}|`;
    const needle = `|${value}|`;
    return haystack.includes(needle);
  }

  #itemMatchesAuthor(item, authorHandle) {
    if (!item || !authorHandle) return false;
    return (
      this.#pipeListContains(item.authors, authorHandle) ||
      this.#pipeListContains(item.editors, authorHandle)
    );
  }
}

if (!customElements.get("author-books")) {
  customElements.define("author-books", AuthorBooks);
}
